import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ApiHttpError, RouteNotFoundError } from "../api/client";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_REASON,
  useWritesBlocked,
} from "../authority";
import {
  confirmRefund,
  createRefundProposal,
  executeRefund,
  getRefundCandidate,
  isBroadcastResponse,
  isCalldataOnlyResponse,
  simulateRefund,
  type RefundCandidateDetailResponse,
  type RefundConfirmResponse,
  type RefundExecuteResponse,
  type RefundProposalResponse,
  type RefundSimulateResponse,
} from "../api/refunds";

// Refund detail + action page.
//
// Five workflow blocks:
//
//   1. Detail   — GET /candidates/:paymentIntentId (auto-load on mount)
//   2. Simulate — POST /simulate (read-only eth_call)
//   3. Proposal — POST /proposals (creates money_movement proposal;
//                  link sends operator to /approvals/:id to sign + collect quorum)
//   4. Execute  — POST / (handles BOTH response modes:
//                   `broadcast` -> show txHash + waiting-for-confirm,
//                   `calldata_only` -> show to/value/data + copy buttons)
//   5. Confirm  — POST /:proposalId/confirm (manual button in this first PR)
//
// Hard contracts enforced visually:
//   * refundToPayer is displayed as a read-only field with a
//     "contract-stored, admin cannot edit" badge.
//   * Execute button is disabled until the operator types a
//     `proposalId` from an approved proposal (no auto-link from
//     /proposals → /execute; the approval-quorum step in
//     /approvals/:id must happen first).
//   * No raw private key / signer secret / HMAC ever touches the
//     browser — every signing primitive is server-side (broadcast
//     mode) or operator's external wallet (calldata-only mode).

function RefundDetailInner({ paymentIntentId }: { paymentIntentId: string }) {
  const [detail, setDetail] = useState<RefundCandidateDetailResponse | null>(null);
  // Hook — declared with the other hooks, above any early return.
  const writesBlocked = useWritesBlocked();
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [sim, setSim] = useState<RefundSimulateResponse | null>(null);
  const [simErr, setSimErr] = useState<string | null>(null);
  const [simBusy, setSimBusy] = useState(false);

  const [prop, setProp] = useState<RefundProposalResponse | null>(null);
  const [propErr, setPropErr] = useState<string | null>(null);
  const [propBusy, setPropBusy] = useState(false);

  const [proposalId, setProposalId] = useState("");
  const [exec, setExec] = useState<RefundExecuteResponse | null>(null);
  const [execErr, setExecErr] = useState<string | null>(null);
  const [execBusy, setExecBusy] = useState(false);

  const [conf, setConf] = useState<RefundConfirmResponse | null>(null);
  const [confErr, setConfErr] = useState<string | null>(null);
  const [confBusy, setConfBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    setDetailErr(null);
    setDetailLoading(true);
    try {
      setDetail(await getRefundCandidate(paymentIntentId));
    } catch (e) {
      setDetailErr(formatError(e));
    } finally {
      setDetailLoading(false);
    }
  }, [paymentIntentId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  async function runSimulate() {
    setSim(null);
    setSimErr(null);
    setSimBusy(true);
    try {
      setSim(await simulateRefund(paymentIntentId));
    } catch (e) {
      setSimErr(formatError(e));
    } finally {
      setSimBusy(false);
    }
  }

  async function runProposal() {
    setProp(null);
    setPropErr(null);
    setPropBusy(true);
    try {
      const r = await createRefundProposal(paymentIntentId);
      setProp(r);
      setProposalId(r.proposalId);
    } catch (e) {
      setPropErr(formatError(e));
    } finally {
      setPropBusy(false);
    }
  }

  async function runExecute() {
    setExec(null);
    setExecErr(null);
    setExecBusy(true);
    try {
      setExec(
        await executeRefund({
          paymentIntentId,
          proposalId,
        }),
      );
    } catch (e) {
      setExecErr(formatError(e));
    } finally {
      setExecBusy(false);
    }
  }

  async function runConfirm() {
    setConf(null);
    setConfErr(null);
    setConfBusy(true);
    try {
      setConf(await confirmRefund(proposalId));
    } catch (e) {
      setConfErr(formatError(e));
    } finally {
      setConfBusy(false);
    }
  }

  const proposalIdValid = proposalId !== "" && proposalId.length <= 128;
  const canExecute = !execBusy && detail !== null && proposalIdValid;
  const canConfirm = !confBusy && proposalIdValid;

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
        Bound to{" "}
        <code>
          GET /v2/admin/bff/escrow/refund/candidates/:paymentIntentId
        </code>{" "}
        + POST /simulate /proposals / /:id/confirm. Bearer session +
        trusted device, no HMAC in browser.
      </p>

      <p style={{ margin: "8px 0" }}>
        <Link to="/escrow/refunds" style={{ color: "var(--accent, #2c7be5)" }}>
          ← Back to candidates
        </Link>
      </p>

      {writesBlocked && (
        <AuthorityNotice scope="Creating, executing and confirming a refund are blocked." />
      )}

      {detailErr && <ErrorBox text={detailErr} />}
      {detailLoading && !detail && (
        <p style={{ color: "var(--fg-dim)" }}>Loading detail…</p>
      )}

      {detail && (
        <>
          <Section title="1. Detail">
            <DetailGrid d={detail} />
          </Section>

          <Section title="2. Simulate (read-only)">
            <p style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 0 }}>
              Runs <code>eth_call refund(bytes32)</code> from the V3 owner
              against the public Base Sepolia RPC. No D1 write beyond the
              standard admin-audit row. No on-chain broadcast.
            </p>
            <button onClick={() => void runSimulate()} disabled={simBusy}>
              {simBusy ? "Simulating…" : "Run simulate"}
            </button>
            {simErr && <ErrorBox text={simErr} />}
            {sim && <SimulateResult r={sim} />}
          </Section>

          <Section title="3. Create proposal (money_movement)">
            <p style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 0 }}>
              Writes one row to <code>admin_owner_action_proposals</code>{" "}
              (action_type: money_movement; payload action:
              escrow_refund). After creation, follow the link to{" "}
              <code>/approvals/:id</code> for each owner wallet to sign
              the EIP-712 approval and reach the 2-of-N threshold.
            </p>
            <button
              onClick={writesBlocked ? undefined : () => void runProposal()}
              disabled={writesBlocked || propBusy || sim?.simulation.ok !== true}
              title={
                writesBlocked
                  ? GAOSAFE_APPROVAL_REASON
                  : sim?.simulation.ok !== true
                    ? "Run simulate first; proposal creation requires a non-revert simulation."
                    : ""
              }
            >
              {propBusy ? "Creating proposal…" : "Create proposal"}
            </button>
            {propErr && <ErrorBox text={propErr} />}
            {prop && <ProposalResult r={prop} />}
          </Section>

          <Section title="4. Execute (after 2-of-N approval)">
            <p style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 0 }}>
              Calls{" "}
              <code>POST /v2/admin/bff/escrow/refund</code>. The BE
              either dispatches via the H-3 signer facade
              (response <code>mode: "broadcast"</code> with tx hash) OR
              returns the encoded calldata for external Safe / hardware
              signing (response <code>mode: "calldata_only"</code>).
              Both modes are handled below.
            </p>
            <fieldset
              style={{
                border: "1px solid var(--border)",
                borderRadius: 4,
                padding: 12,
                display: "grid",
                gridTemplateColumns: "120px 1fr",
                gap: 8,
              }}
            >
              <label style={{ alignSelf: "center" }}>proposalId</label>
              <input
                value={proposalId}
                onChange={(e) => setProposalId(e.target.value.trim())}
                placeholder="ulid of an APPROVED proposal (collect quorum at /approvals/:id first)"
              />
              <div />
              <button
                onClick={writesBlocked ? undefined : () => void runExecute()}
                disabled={writesBlocked || !canExecute}
                title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
              >
                {execBusy ? "Submitting…" : "Execute"}
              </button>
            </fieldset>
            {execErr && <ErrorBox text={execErr} />}
            {exec && <ExecuteResult r={exec} />}
          </Section>

          <Section title="5. Confirm (after broadcast)">
            <p style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 0 }}>
              Calls{" "}
              <code>POST /v2/admin/bff/escrow/refund/:proposalId/confirm</code>.
              The BE re-fetches the tx receipt, verifies ≥ 3
              confirmations + tx success + Refunded event + on-chain
              REFUNDED status, then reconciles D1 idempotently. Manual
              button for now; future PR may add auto-polling.
            </p>
            <button
              onClick={writesBlocked ? undefined : () => void runConfirm()}
              disabled={writesBlocked || !canConfirm}
              title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
            >
              {confBusy ? "Confirming…" : "Confirm"}
            </button>
            {confErr && <ErrorBox text={confErr} />}
            {conf && <ConfirmResult r={conf} />}
          </Section>
        </>
      )}
    </div>
  );
}

export function RefundDetail() {
  const { paymentIntentId } = useParams<{ paymentIntentId: string }>();
  return (
    <div>
      <PageHeader
        title={`Refund — ${paymentIntentId ?? ""}`}
        subtitle="Detail · simulate · propose · execute · confirm. Refund destination is the contract-stored payer (read-only)."
        method="GET"
        path="/v2/admin/bff/escrow/refund/candidates/:paymentIntentId"
      />
      <AuthRequired>
        {paymentIntentId ? (
          <RefundDetailInner paymentIntentId={paymentIntentId} />
        ) : (
          <ErrorBox text="Missing paymentIntentId in URL" />
        )}
      </AuthRequired>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "16px 16px 12px 16px",
        margin: "16px 0",
        background: "var(--bg-card)",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", fontSize: 14 }}>{title}</h3>
      {children}
    </section>
  );
}

function ReadOnlyPayerBadge() {
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 6px",
        marginLeft: 8,
        borderRadius: 3,
        background: "rgba(74,222,128,0.08)",
        color: "var(--ok, #0a9444)",
        border: "1px solid var(--ok, #0a9444)",
      }}
      title="Contract-stored payer. Admin cannot change destination."
    >
      read-only · contract-stored
    </span>
  );
}

function DetailGrid({ d }: { d: RefundCandidateDetailResponse }) {
  const c = d.candidate;
  const o = d.onchain;
  const r = d.refundDestination;
  const checks = d.safetyChecks;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <tbody>
        <Row k="paymentIntentId" v={c.paymentIntentId} />
        <Row k="domain" v={c.domain} />
        <Row k="status" v={c.status} />
        <Row k="failureReason" v={c.failureReason ?? "—"} />
        <Row k="escrow" v={`${c.escrowContract} (${c.escrowVersion})`} />
        <Row k="invoiceId" v={c.invoiceId} />
        <Row
          k="payer"
          v={
            <>
              <code>{c.intent.payerWallet}</code>
              <ReadOnlyPayerBadge />
            </>
          }
        />
        <Row k="buyer" v={<code>{c.intent.ownerWallet}</code>} />
        <Row
          k="token"
          v={
            <>
              <code>{c.token.address}</code> · {c.token.symbol} ·{" "}
              {c.token.decimals} dec
            </>
          }
        />
        <Row
          k="amount"
          v={
            <>
              {c.amount.display} {c.token.symbol}{" "}
              <span style={{ color: "var(--fg-dim)", fontSize: 11 }}>
                ({c.amount.baseUnits} base)
              </span>
            </>
          }
        />
        <Row k="depositTxHash" v={<code>{c.depositTxHash}</code>} />
        <Row
          k="billing"
          v={
            c.billing
              ? `${c.billing.status} (source: ${c.billing.sourceService ?? "—"})`
              : "—"
          }
        />
        <Row k="createdAt" v={c.createdAt} />
        <Row k="updatedAt" v={c.updatedAt} />
        {o && (
          <>
            <SectionRow label="On-chain (live read)" />
            <Row k="onchain.payer" v={<code>{o.payer}</code>} />
            <Row k="onchain.buyer" v={<code>{o.buyer}</code>} />
            <Row k="onchain.paymentToken" v={<code>{o.paymentToken}</code>} />
            <Row k="onchain.grossAmount" v={o.grossAmount} />
            <Row
              k="onchain.status"
              v={`${o.status} (${describeOnchainStatus(o.status)})`}
            />
            <Row k="onchain.isPending" v={String(o.isPending)} />
            <Row
              k="onchain.refundedAt"
              v={o.refundedAt === "0" ? "—" : o.refundedAt}
            />
          </>
        )}
        {r && (
          <>
            <SectionRow label="Refund destination (read-only)" />
            <Row
              k="refundDestination.address"
              v={
                <>
                  <code>{r.address}</code>
                  <ReadOnlyPayerBadge />
                </>
              }
            />
            <Row k="refundDestination.editable" v="false" />
            <Row k="refundDestination.source" v={r.source} />
          </>
        )}
        <SectionRow label="Safety checks" />
        <Row
          k="escrowOnTrustedList"
          v={renderBool(checks.escrowOnTrustedList)}
        />
        <Row k="onchainIsPending" v={renderTriBool(checks.onchainIsPending)} />
        <Row
          k="onchainStatusDeposited"
          v={renderTriBool(checks.onchainStatusDeposited)}
        />
        <Row
          k="onchainGrossAmountMatches"
          v={renderTriBool(checks.onchainGrossAmountMatches)}
        />
        <Row
          k="onchainTokenMatches"
          v={renderTriBool(checks.onchainTokenMatches)}
        />
      </tbody>
    </table>
  );
}

function describeOnchainStatus(s: number): string {
  switch (s) {
    case 0:
      return "NONE";
    case 1:
      return "DEPOSITED";
    case 2:
      return "SETTLED";
    case 3:
      return "REFUNDED";
    default:
      return "?";
  }
}

function renderBool(b: boolean) {
  return (
    <span style={{ color: b ? "var(--ok, #0a9444)" : "var(--danger, #d23f3f)" }}>
      {String(b)}
    </span>
  );
}

function renderTriBool(b: boolean | null) {
  if (b === null) return <em style={{ color: "var(--fg-dim)" }}>unknown</em>;
  return renderBool(b);
}

function SimulateResult({ r }: { r: RefundSimulateResponse }) {
  const ok = r.simulation.ok;
  return (
    <fieldset
      style={{
        border: `1px solid var(${ok ? "--ok" : "--danger"})`,
        borderRadius: 4,
        padding: 12,
        marginTop: 12,
        background: ok ? "rgba(74,222,128,0.05)" : "rgba(255,100,100,0.05)",
      }}
    >
      <legend
        style={{
          padding: "0 6px",
          color: ok ? "var(--ok)" : "var(--danger)",
        }}
      >
        {ok ? "Simulation: non-revert" : "Simulation: failed"}
      </legend>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          <Row k="selector" v={<code>{r.selector}</code>} />
          <Row
            k="calldata"
            v={
              <CopyableHex value={r.calldata} ariaLabel="copy calldata" />
            }
          />
          <Row
            k="refundToPayer"
            v={
              <>
                <code>{r.refundToPayer}</code>
                <ReadOnlyPayerBadge />
              </>
            }
          />
          <Row k="amount.baseUnits" v={r.amount.baseUnits} />
          <Row k="onchain.isPending" v={String(r.onchain.isPending)} />
          <Row k="onchain.status" v={String(r.onchain.status)} />
          <Row
            k="simulation.ownerSimulatedFrom"
            v={<code>{r.simulation.ownerSimulatedFrom}</code>}
          />
          {r.simulation.error && (
            <Row
              k="simulation.error"
              v={<span style={{ color: "var(--danger)" }}>{r.simulation.error}</span>}
            />
          )}
        </tbody>
      </table>
    </fieldset>
  );
}

function ProposalResult({ r }: { r: RefundProposalResponse }) {
  return (
    <fieldset
      style={{
        border: "1px solid var(--ok)",
        borderRadius: 4,
        padding: 12,
        marginTop: 12,
        background: "rgba(74,222,128,0.05)",
      }}
    >
      <legend style={{ padding: "0 6px", color: "var(--ok)" }}>
        Proposal created
      </legend>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          <Row
            k="proposalId"
            v={
              <Link
                to={`/approvals/${encodeURIComponent(r.proposalId)}`}
                style={{ color: "var(--accent, #2c7be5)" }}
              >
                {r.proposalId}
              </Link>
            }
          />
          <Row k="status" v={r.proposal.status} />
          <Row k="actionDigest" v={<code>{r.actionDigest}</code>} />
          <Row k="payloadHash" v={<code>{r.payloadHash}</code>} />
          <SectionRow label="Decoded payload (BE-canonical)" />
          <Row k="payload.action" v={r.payload.action} />
          <Row k="payload.paymentIntentId" v={r.payload.paymentIntentId} />
          <Row k="payload.invoiceId" v={<code>{r.payload.invoiceId}</code>} />
          <Row
            k="payload.escrowAddress"
            v={<code>{r.payload.escrowAddress}</code>}
          />
          <Row k="payload.escrowVersion" v={r.payload.escrowVersion} />
          <Row k="payload.selector" v={<code>{r.payload.selector}</code>} />
          <Row
            k="payload.calldata"
            v={<CopyableHex value={r.payload.calldata} ariaLabel="copy calldata" />}
          />
          <Row
            k="payload.refundToPayer"
            v={
              <>
                <code>{r.payload.refundToPayer}</code>
                <ReadOnlyPayerBadge />
              </>
            }
          />
          <Row k="payload.amountBaseUnits" v={r.payload.amountBaseUnits} />
          <Row k="payload.chainId" v={String(r.payload.chainId)} />
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 12 }}>
        Next:{" "}
        <Link
          to={`/approvals/${encodeURIComponent(r.proposalId)}`}
          style={{ color: "var(--accent, #2c7be5)" }}
        >
          /approvals/{r.proposalId}
        </Link>{" "}
        — each owner wallet signs the EIP-712 approval to reach the
        threshold.
      </div>
    </fieldset>
  );
}

function ExecuteResult({ r }: { r: RefundExecuteResponse }) {
  if (isBroadcastResponse(r)) {
    return (
      <fieldset
        style={{
          border: "1px solid var(--ok)",
          borderRadius: 4,
          padding: 12,
          marginTop: 12,
          background: "rgba(74,222,128,0.05)",
        }}
      >
        <legend style={{ padding: "0 6px", color: "var(--ok)" }}>
          Mode: broadcast — tx submitted
        </legend>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <Row k="mode" v="broadcast" />
            <Row k="txHash" v={<code>{r.txHash}</code>} />
            <Row k="blockNumber" v={String(r.blockNumber)} />
            <Row k="invoiceId" v={<code>{r.invoiceId}</code>} />
            <Row
              k="refundToPayer"
              v={
                <>
                  <code>{r.refundToPayer}</code>
                  <ReadOnlyPayerBadge />
                </>
              }
            />
            <Row k="amountBaseUnits" v={r.amountBaseUnits} />
            <Row k="proposalId" v={r.proposalId} />
          </tbody>
        </table>
        <p style={{ marginTop: 10, fontSize: 12, color: "var(--fg-dim)" }}>
          Waiting for confirmations… run <strong>Confirm</strong> below
          once Base Sepolia has produced ≥ 3 blocks past the receipt.
        </p>
      </fieldset>
    );
  }
  if (isCalldataOnlyResponse(r)) {
    return (
      <fieldset
        style={{
          border: "1px solid var(--warn)",
          borderRadius: 4,
          padding: 12,
          marginTop: 12,
          background: "rgba(251,191,36,0.06)",
        }}
      >
        <legend style={{ padding: "0 6px", color: "var(--warn)" }}>
          Mode: calldata-only fallback
        </legend>
        <p style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 0 }}>
          Signer policy is not enabled for{" "}
          <code>refund(bytes32)</code>, OR the server-side signer is
          unconfigured (reason: <code>{r.reason}</code>). Paste the
          calldata below into Safe / Frame / hardware wallet signed
          by the V3 owner key. The browser does <strong>NOT</strong>{" "}
          attempt any direct contract write.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <Row k="mode" v="calldata_only" />
            <Row k="reason" v={<code>{r.reason}</code>} />
            <Row k="message" v={r.message} />
            <Row
              k="To"
              v={<CopyableHex value={r.to} ariaLabel="copy to address" />}
            />
            <Row k="Value" v={r.value} />
            <Row
              k="Data"
              v={<CopyableHex value={r.data} ariaLabel="copy calldata" />}
            />
            <Row
              k="refundToPayer"
              v={
                <>
                  <code>{r.refundToPayer}</code>
                  <ReadOnlyPayerBadge />
                </>
              }
            />
            <Row k="amountBaseUnits" v={r.amountBaseUnits} />
            <Row k="chainId" v={String(r.chainId)} />
            <Row k="escrowVersion" v={r.escrowVersion} />
          </tbody>
        </table>
        <p
          style={{
            marginTop: 10,
            padding: 10,
            background: "rgba(251,191,36,0.1)",
            border: "1px solid var(--warn)",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          ⚠️ Use Safe / operator wallet to sign and broadcast. The
          proposal remains in <code>approved</code> state and is NOT
          marked executed until the BE later confirms the tx. After
          your external broadcast lands and reaches ≥ 3 confirmations,
          re-press <strong>Execute</strong> (or run a dedicated admin
          ingest step — out of scope for this PR) so the BE records
          the operator-signed txHash. Then run <strong>Confirm</strong>.
        </p>
      </fieldset>
    );
  }
  // Unreachable: type narrowing covers both modes.
  return null;
}

function ConfirmResult({ r }: { r: RefundConfirmResponse }) {
  return (
    <fieldset
      style={{
        border: "1px solid var(--ok)",
        borderRadius: 4,
        padding: 12,
        marginTop: 12,
        background: "rgba(74,222,128,0.05)",
      }}
    >
      <legend style={{ padding: "0 6px", color: "var(--ok)" }}>
        Confirmed — D1 reconciled
      </legend>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          <Row k="proposalId" v={r.proposalId} />
          <Row k="paymentIntentId" v={r.paymentIntentId} />
          <Row k="txHash" v={<code>{r.txHash}</code>} />
          <Row k="confirmations" v={String(r.confirmations)} />
          <Row k="confirmedBlock" v={String(r.confirmedBlock)} />
          <Row k="refundedAt (unix)" v={r.refundedAt} />
          <Row
            k="refundToPayer"
            v={
              <>
                <code>{r.refundToPayer}</code>
                <ReadOnlyPayerBadge />
              </>
            }
          />
          <Row k="amountBaseUnits" v={r.amountBaseUnits} />
          <Row k="billingStatus" v={r.billingStatus} />
          <Row k="paymentIntentStatus" v={r.paymentIntentStatus} />
          <Row k="domainReleased" v={renderBool(r.domainReleased)} />
        </tbody>
      </table>
      <p style={{ marginTop: 10, fontSize: 12, color: "var(--fg-dim)" }}>
        Billing flipped to REFUNDED; payment_intents flipped to REFUNDED
        (deposit_tx_hash preserved); payment_events &amp;
        domain_audit_logs rows appended idempotently; BE-A
        unresolved-deposit guard no longer matches this row, so the
        domain is now available for retry.
      </p>
    </fieldset>
  );
}

// ── Row / hex / error helpers ──────────────────────────────────────

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "6px 8px", color: "var(--fg-dim)", width: 260 }}>{k}</td>
      <td style={{ padding: "6px 8px", wordBreak: "break-all" }}>{v}</td>
    </tr>
  );
}

function SectionRow({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={2}
        style={{
          padding: "10px 8px 4px 8px",
          fontWeight: 600,
          fontSize: 12,
          color: "var(--fg-dim)",
          borderTop: "2px solid var(--border)",
        }}
      >
        {label}
      </td>
    </tr>
  );
}

function CopyableHex({ value, ariaLabel }: { value: string; ariaLabel: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
      <code style={{ wordBreak: "break-all" }}>{value}</code>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={async () => {
          try {
            if (typeof navigator !== "undefined" && navigator.clipboard) {
              await navigator.clipboard.writeText(value);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // Silently ignore clipboard failures — operator can manually copy.
          }
        }}
        style={{ fontSize: 10, padding: "2px 8px" }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: 12,
        border: "1px solid var(--danger)",
        color: "var(--danger)",
        borderRadius: 4,
        background: "rgba(255,100,100,0.08)",
      }}
    >
      {text}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof RouteNotFoundError) return `BE route missing: ${e.message}`;
  if (e instanceof ApiHttpError) {
    const reason =
      typeof e.body === "object" && e.body !== null && "reason" in e.body
        ? String((e.body as { reason: unknown }).reason)
        : null;
    return `HTTP ${e.status} on ${e.path}${reason ? ` (${reason})` : ""}`;
  }
  return e instanceof Error ? e.message : String(e);
}
